/**
 * Date-range presets for the period filter chips.
 *
 * "Week over week" — Period A = last completed Mon–Sun;
 *                    Period B = the week before that.
 *
 * "Month over month" — Period A = the whole previous calendar month;
 *                       Period B = the month before that.
 *
 * Quarter and year preset variants are reachable via the custom
 * date-range picker; not exposed as one-click presets per spec.
 */
import {
  endOfMonth,
  endOfWeek,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
} from 'date-fns';

export const PRESET_IDS = {
  custom: 'custom',
  weekOverWeek: 'wow',
  monthOverMonth: 'mom',
};

const ISO_WEEK_OPTIONS = { weekStartsOn: 1 }; // Monday

const lastCompletedWeek = (today) => {
  // Roll back into the previous week so "Period A" is always a
  // *finished* week — never a partial one that's still accruing
  // completions today.
  const previousWeekAnchor = subDays(today, 7);
  return {
    startDate: startOfWeek(previousWeekAnchor, ISO_WEEK_OPTIONS),
    endDate: endOfWeek(previousWeekAnchor, ISO_WEEK_OPTIONS),
  };
};

const previousMonth = (today) => {
  const anchor = subMonths(today, 1);
  return { startDate: startOfMonth(anchor), endDate: endOfMonth(anchor) };
};

/**
 * Returns `{ periodA, periodB }` for the given preset, anchored on
 * the supplied `today` (or `new Date()`). Each period is a
 * `{ startDate, endDate }` pair of native `Date` instances.
 */
export const buildPresetRanges = (presetId, today = new Date()) => {
  if (presetId === PRESET_IDS.weekOverWeek) {
    const periodA = lastCompletedWeek(today);
    const weekBeforeAnchor = subDays(periodA.startDate, 1);
    const periodB = {
      startDate: startOfWeek(weekBeforeAnchor, ISO_WEEK_OPTIONS),
      endDate: endOfWeek(weekBeforeAnchor, ISO_WEEK_OPTIONS),
    };
    return { periodA, periodB };
  }

  if (presetId === PRESET_IDS.monthOverMonth) {
    const periodA = previousMonth(today);
    const monthBeforeAnchor = subMonths(periodA.startDate, 1);
    const periodB = {
      startDate: startOfMonth(monthBeforeAnchor),
      endDate: endOfMonth(monthBeforeAnchor),
    };
    return { periodA, periodB };
  }

  return null;
};

const isoDay = (date) => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const formatRangeForApi = (range) => {
  if (!range?.startDate || !range?.endDate) return null;
  return { start: isoDay(range.startDate), end: isoDay(range.endDate) };
};

const SHORT_DATE = { month: 'short', day: 'numeric' };

export const formatRangeChipLabel = (range) => {
  if (!range?.startDate || !range?.endDate) return '';
  const a = range.startDate.toLocaleDateString(undefined, SHORT_DATE);
  const b = range.endDate.toLocaleDateString(undefined, SHORT_DATE);
  return `${a} – ${b}`;
};

/**
 * Heuristic: return the preset id that *currently* matches the two
 * ranges, or 'custom' if neither preset is a fit. Lets the page
 * keep the preset chip in sync after the user edits a date range
 * by hand.
 */
export const detectActivePreset = (periodA, periodB, today = new Date()) => {
  const sameRange = (a, b) =>
    a?.startDate &&
    b?.startDate &&
    isoDay(a.startDate) === isoDay(b.startDate) &&
    isoDay(a.endDate) === isoDay(b.endDate);

  for (const id of [PRESET_IDS.weekOverWeek, PRESET_IDS.monthOverMonth]) {
    const preset = buildPresetRanges(id, today);
    if (preset && sameRange(preset.periodA, periodA) && sameRange(preset.periodB, periodB)) {
      return id;
    }
  }
  return PRESET_IDS.custom;
};
